import React, { useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CHANNEL_BREAKDOWN,
  MAX_LEGEND_SEGMENTS,
  OTHER_LABEL,
  buildStackedRows,
  collectSegmentNames,
  limitSegmentNames,
  segmentColor,
  segmentDataKey,
} from "./salesHistoryBreakdown";

const CHART_MARGIN = { top: 8, right: 12, left: 0, bottom: 0 };

const formatNumber = (value, digits = 0) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  return Number(value).toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
};

export const formatMonth = (value) => {
  if (!value) return "";
  return new Date(`${value}-01T00:00:00.000Z`).toLocaleDateString(undefined, {
    month: "short",
    year: "2-digit",
    timeZone: "UTC",
  });
};

/**
 * Same swatch styling as Sales Analysis's LegendItem, inlined so this chart
 * does not pull the metrics dark-mode context into the SKU modal.
 */
const LegendSwatch = ({ color, label }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.7 }}>
    <Box
      component="span"
      sx={{
        width: 9,
        height: 9,
        borderRadius: "2px",
        backgroundColor: color,
        display: "inline-block",
      }}
    />
    <Typography
      component="span"
      sx={{ fontSize: 11.5, color: "text.secondary" }}
    >
      {label}
    </Typography>
  </Box>
);

export const ChartTip = ({ active, payload, label, hint, stacked }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload || {};
  const noEvidence = point.observed === null || point.observed === undefined;
  return (
    <Paper
      elevation={3}
      sx={{
        px: 1.5,
        py: 1.1,
        borderRadius: 1,
        bgcolor: "rgba(33, 33, 33, 0.94)",
        color: "#fff",
        minWidth: 200,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5, color: "#fff" }}>
        {formatMonth(label)}
      </Typography>
      {noEvidence ? (
        <Typography sx={{ fontSize: 11, color: "#ffe082" }}>
          No sales evidence this month. Not treated as zero.
        </Typography>
      ) : (
        <>
          <Typography sx={{ fontSize: 11, color: "#fff" }}>
            Units sold: {formatNumber(point.observed)}
          </Typography>
          {stacked &&
            (point.segments || []).map((part) => (
              <Box
                key={part.name}
                sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.35 }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "2px",
                    backgroundColor: part.color,
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: 11, color: "#e0e0e0" }}>
                  {part.name}: {formatNumber(part.units_sold)}
                </Typography>
              </Box>
            ))}
          {hint && (
            <Typography sx={{ fontSize: 10.5, color: "#b0bec5", mt: 0.5 }}>
              {hint}
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
};

const SalesHistoryChart = ({
  months = [],
  onMonthClick,
  selectedMonth = null,
  sourceOptions = [],
  dimension = CHANNEL_BREAKDOWN,
  segmentOptions = null,
  colorOrder = null,
}) => {
  const theme = useTheme();
  const monthlyData = useMemo(() => months || [], [months]);
  const observedColor = theme.palette.error.main;
  const selectedColor = theme.palette.primary.main;

  // Segments that actually contributed, ordered by the option list so colour
  // and stack order stay stable as the data changes.
  const options = segmentOptions || sourceOptions;
  const present = useMemo(
    () => collectSegmentNames(monthlyData, dimension, options),
    [monthlyData, dimension, options],
  );
  const stackSources = useMemo(
    () => limitSegmentNames(present, monthlyData, dimension),
    [present, monthlyData, dimension],
  );

  const hasOther = stackSources.includes(OTHER_LABEL);
  // Without breakdown evidence the chart stays exactly as it was: one bar.
  const stacked = present.length > 0;
  // Dense colour index: position among the segments actually drawn, so hues
  // stay maximally separated and can never wrap onto a duplicate. Shared with
  // the sibling chart when the caller supplies one.
  const colorKeys = colorOrder || stackSources;

  const chartData = useMemo(
    () =>
      stacked
        ? buildStackedRows({
            rows: monthlyData,
            dimension,
            names: stackSources,
            options: colorKeys,
            isBlank: (row) =>
              row.observed === null || row.observed === undefined,
          })
        : monthlyData,
    [monthlyData, stackSources, stacked, colorKeys, dimension],
  );

  const clickable = typeof onMonthClick === "function";
  const handleBarClick = (entry) => {
    if (!clickable) return;
    const month = entry?.payload?.month || entry?.month;
    const observed = entry?.payload?.observed ?? entry?.observed;
    if (!month || observed === null || observed === undefined) return;
    onMonthClick(month);
  };

  const cellOpacity = (month) =>
    selectedMonth && month !== selectedMonth ? 0.35 : 0.9;

  if (!monthlyData.length) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary", py: 4, textAlign: "center" }}>
        No sales evidence in this range.
      </Typography>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          gap: 1.75,
          alignItems: "center",
          flexWrap: "wrap",
          mb: 0.75,
        }}
      >
        {stacked ? (
          stackSources.map((source) => (
            <LegendSwatch
              key={source}
              color={segmentColor(source, colorKeys)}
              label={source}
            />
          ))
        ) : (
          <LegendSwatch color={observedColor} label="Units sold" />
        )}
      </Box>

      <Box sx={{ height: { xs: 280, sm: 360 } }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={CHART_MARGIN}>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke={theme.palette.divider}
            />
            <XAxis
              dataKey="month"
              tickFormatter={formatMonth}
              minTickGap={18}
              tick={{ fontSize: 11, fontFamily: "Poppins" }}
            />
            <YAxis
              tickFormatter={(value) => formatNumber(value)}
              width={46}
              tick={{ fontSize: 11, fontFamily: "Poppins" }}
            />
            <RechartsTooltip
              content={
                <ChartTip
                  stacked={stacked}
                  hint={clickable ? "Click for daily detail" : null}
                />
              }
            />
            {stacked ? (
              stackSources.map((source, index) => {
                const color = segmentColor(source, colorKeys);
                const isTop = index === stackSources.length - 1;
                return (
                  <Bar
                    key={source}
                    dataKey={segmentDataKey(source)}
                    name={source}
                    stackId="units"
                    fill={color}
                    maxBarSize={26}
                    radius={isTop ? [3, 3, 0, 0] : undefined}
                    onClick={handleBarClick}
                    cursor={clickable ? "pointer" : undefined}
                  >
                    {chartData.map((row) => (
                      <Cell
                        key={row.month}
                        fill={row.month === selectedMonth ? selectedColor : color}
                        fillOpacity={cellOpacity(row.month)}
                      />
                    ))}
                  </Bar>
                );
              })
            ) : (
              <Bar
                dataKey="observed"
                name="Units sold"
                fill={observedColor}
                radius={[3, 3, 0, 0]}
                maxBarSize={26}
                fillOpacity={0.85}
                onClick={handleBarClick}
                cursor={clickable ? "pointer" : undefined}
              >
                {monthlyData.map((row) => (
                  <Cell
                    key={row.month}
                    fill={row.month === selectedMonth ? selectedColor : observedColor}
                    fillOpacity={
                      selectedMonth && row.month !== selectedMonth ? 0.45 : 0.85
                    }
                  />
                ))}
              </Bar>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
      {hasOther && (
        <Typography sx={{ fontSize: 11, color: theme.palette.text.secondary, mt: 0.5 }}>
          Smaller {dimension.noun} beyond the top {MAX_LEGEND_SEGMENTS} are
          grouped as “{OTHER_LABEL}”.
        </Typography>
      )}
    </Box>
  );
};

export default SalesHistoryChart;
